import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Badge } from '@caterinx/ui/components/ui/badge';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@caterinx/ui/components/ui/select';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Loader2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchTeam, inviteTeamMember, removeTeamMember, type TeamMember } from '../lib/supplier.js';

const ROLE_LABELS: Record<TeamMember['role'], string> = {
  owner: 'Inhaber',
  admin: 'Administrator',
  member: 'Mitarbeiter',
};

/**
 * Mitarbeiterverwaltung eines Betriebs.
 *
 * Mitarbeiter und offene Einladungen stehen in **einer** Liste. Zwei getrennte Listen laden
 * dieselbe Frage zweimal ein — „warum steht Frau Meier nicht drin?" hat dann die Antwort
 * „weil sie noch nicht bestätigt hat", und die steht in der anderen Liste, die niemand
 * aufklappt.
 *
 * Die Rolle `owner` fehlt in der Auswahl mit Absicht: Es gibt genau einen Inhaber, und er
 * entsteht beim Anlegen des Betriebs. Eine Einladung, die einen zweiten erzeugt, wäre ein Weg,
 * sich selbst aus dem eigenen Konto auszusperren.
 */
export function TeamPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');

  const { data, isPending, isError } = useQuery({ queryKey: ['supplier-team'], queryFn: fetchTeam });

  const invite = useMutation({
    mutationFn: () => inviteTeamMember({ email: email.trim(), name: name.trim() || undefined, role }),
    onSuccess: () => {
      toast({ title: 'Einladung verschickt', description: `An ${email.trim()}.` });
      setEmail('');
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['supplier-team'] });
    },
    onError: (error) => {
      const payload = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({
        variant: 'destructive',
        title: 'Einladung fehlgeschlagen',
        description: payload?.message ?? 'Bitte prüfen Sie die Adresse.',
      });
    },
  });

  const remove = useMutation({
    mutationFn: (member: TeamMember) => removeTeamMember(member.status === 'invited' ? member.email : member.userId),
    onSuccess: () => {
      toast({ title: 'Entfernt' });
      void queryClient.invalidateQueries({ queryKey: ['supplier-team'] });
    },
    onError: (error) => {
      const payload = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({ variant: 'destructive', title: 'Entfernen fehlgeschlagen', description: payload?.message });
    },
  });

  return (
    <main className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-2">
        <Link to="/" className="text-muted-foreground inline-flex items-center gap-1 text-sm hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zurück zur Übersicht
        </Link>
        <h1 className="text-2xl font-semibold">Mitarbeiter</h1>
        <p className="text-muted-foreground text-sm">
          Wer hier steht, sieht eingehende Ausschreibungen und kann Angebote abgeben.
        </p>
      </header>

      {isPending && <Skeleton className="h-48 w-full" />}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Team konnte nicht geladen werden</AlertTitle>
          <AlertDescription>Bitte laden Sie die Seite neu.</AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Im Betrieb</CardTitle>
              <CardDescription>{data.members.length} Person(en), offene Einladungen eingerechnet.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-border divide-y">
                {data.members.map((member) => (
                  <li key={member.userId || member.email} className="flex flex-wrap items-center gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {member.name || member.email}
                        {member.isSelf && <span className="text-muted-foreground font-normal"> (Sie)</span>}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{member.email}</p>
                    </div>

                    <Badge variant={member.status === 'invited' ? 'outline' : 'secondary'}>
                      {member.status === 'invited' ? 'eingeladen' : ROLE_LABELS[member.role]}
                    </Badge>

                    {data.canManage && !member.isSelf && member.role !== 'owner' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(member)}
                      >
                        {member.status === 'invited' ? 'Zurückziehen' : 'Entfernen'}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {data.canManage ? (
            <Card>
              <CardHeader>
                <CardTitle>Kollegen einladen</CardTitle>
                <CardDescription>
                  Die eingeladene Person vergibt ihr eigenes Passwort. Der Link ist 14 Tage gültig.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  className="space-y-4"
                  onSubmit={(formEvent) => {
                    formEvent.preventDefault();
                    invite.mutate();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">E-Mail</Label>
                    <Input
                      id="inviteEmail"
                      type="email"
                      required
                      inputMode="email"
                      value={email}
                      onChange={(changeEvent) => setEmail(changeEvent.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inviteName">Name (optional)</Label>
                    <Input id="inviteName" value={name} onChange={(changeEvent) => setName(changeEvent.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inviteRole">Rolle</Label>
                    <Select value={role} onValueChange={(value) => setRole(value as 'admin' | 'member')}>
                      <SelectTrigger id="inviteRole">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Mitarbeiter — Angebote abgeben</SelectItem>
                        <SelectItem value="admin">Administrator — zusätzlich Stammdaten und Team</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="min-h-11 w-full" disabled={invite.isPending}>
                    {invite.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    Einladung senden
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertTitle>Nur Inhaber und Administratoren</AlertTitle>
              <AlertDescription>
                Sie können das Team sehen, aber nicht ändern. Wenden Sie sich an den Inhaber Ihres Betriebs.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </main>
  );
}
